import { useState, useMemo, FC } from 'react';
import { Button, Popover } from '@servicetitan/design-system';
import classNames from 'classnames';
import { Icon } from '@servicetitan/anvil2';
import ErrorIcon from '@servicetitan/anvil2/assets/icons/material/round/error.svg';
import { AudioDevicePanel } from '@modules/SoftPhoneModule';
import { useAudioDevices } from '@modules/SoftPhoneModule/useAudioDevices';
import { useFeatureFlags } from '@modules/FeatureFlagModule';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule/featureFlagKeys';
import { inboxLocators } from '@locators/inbox';
import { CallControlsTooltip } from '../CallControlsTooltip';
import callControlStyles from '../CallControls.module.css';
import styles from './MicrophoneControl.module.css';

interface MicrophoneControlProps {
  isAudioMuted: boolean;
  phoneConnected: boolean;
  disabled: boolean;
  onMute: VoidFunction;
  onUnmute: VoidFunction;
}

/**
 * Renders the microphone control in the call bar.
 *
 * When the `featureAudioDeviceManagement` flag is enabled and the phone is
 * connected, it expands into a split control: a mute/unmute button paired with
 * a dropdown arrow that opens the audio device selection panel. A warning icon
 * is shown when microphone permission has been denied.
 *
 * When the flag is off or the phone is disconnected, it renders as a plain
 * mute/unmute button.
 */
const MicrophoneControl: FC<MicrophoneControlProps> = ({
  isAudioMuted,
  phoneConnected,
  disabled,
  onMute,
  onUnmute,
}) => {
  const [isAudioDevicesOpen, setIsAudioDevicesOpen] = useState(false);
  const { errorCategory } = useAudioDevices();
  const {
    [FeatureFlagKeys.featureAudioDeviceManagement]:
      featureAudioDeviceManagement,
  } = useFeatureFlags();

  const micButtonConfig = useMemo(
    () =>
      isAudioMuted
        ? {
            tooltipText: 'Unmute',
            tooltipContentId: inboxLocators.callBar.unmuteBtnTooltip,
            ariaLabel: 'Unmute',
            className: callControlStyles.callControlButtonActiveCritical,
            iconName: 'mic_off' as const,
            id: inboxLocators.callBar.unmuteBtn,
            onClick: onUnmute,
          }
        : {
            tooltipText: 'Mute',
            tooltipContentId: inboxLocators.callBar.muteBtnTooltip,
            ariaLabel: 'Mute',
            className: callControlStyles.callControlButtonDefault,
            iconName: 'mic' as const,
            id: inboxLocators.callBar.muteBtn,
            onClick: onMute,
          },
    [isAudioMuted, onMute, onUnmute],
  );

  const isAudioDeviceEnabled =
    Boolean(featureAudioDeviceManagement) && phoneConnected;
  const showWarningIcon = errorCategory === 'permission_denied';

  return (
    <Popover
      caret={{ show: true, className: styles.audioDevicePopoverCaret }}
      padding={null}
      width='auto'
      onClickOutside={() => setIsAudioDevicesOpen(false)}
      popoverContentClassName={styles.audioDevicePopover}
      open={isAudioDeviceEnabled && isAudioDevicesOpen}
      trigger={
        isAudioDeviceEnabled ? (
          <div
            className={classNames(
              styles.micWrapper,
              isAudioMuted ? styles.micWrapperMuted : styles.micWrapperDefault,
            )}
          >
            <CallControlsTooltip
              text={micButtonConfig.tooltipText}
              contentId={micButtonConfig.tooltipContentId}
            >
              <div className={styles.micButtonWrapper}>
                <Button
                  xsmall
                  aria-label={micButtonConfig.ariaLabel}
                  className={styles.micInnerButton}
                  iconName={micButtonConfig.iconName}
                  id={micButtonConfig.id}
                  onClick={micButtonConfig.onClick}
                  disabled={disabled}
                />
                {showWarningIcon && (
                  <Icon
                    svg={ErrorIcon}
                    className={styles.micWarningIcon}
                    data-testid='mic-permission-warning-icon'
                  />
                )}
              </div>
            </CallControlsTooltip>
            <CallControlsTooltip
              text={
                isAudioDevicesOpen ? 'Close Audio Settings' : 'Audio Settings'
              }
            >
              <Button
                xsmall
                aria-label='Audio Device Settings'
                className={classNames(
                  styles.micInnerButton,
                  styles.audioDeviceArrowBtn,
                )}
                iconName='keyboard_arrow_down'
                id={inboxLocators.callBar.audioDeviceBtn}
                disabled={disabled}
                onClick={() => setIsAudioDevicesOpen(!isAudioDevicesOpen)}
              />
            </CallControlsTooltip>
          </div>
        ) : (
          <CallControlsTooltip
            text={micButtonConfig.tooltipText}
            contentId={micButtonConfig.tooltipContentId}
          >
            <Button
              aria-label={micButtonConfig.ariaLabel}
              className={micButtonConfig.className}
              iconName={micButtonConfig.iconName}
              id={micButtonConfig.id}
              onClick={micButtonConfig.onClick}
              disabled={disabled}
            />
          </CallControlsTooltip>
        )
      }
    >
      <AudioDevicePanel onDeviceSelected={() => setIsAudioDevicesOpen(false)} />
    </Popover>
  );
};

export default MicrophoneControl;
